// File: StepOne.js
import React, { useEffect, useState } from "react";
import { Form, Button, Image, Icon, Label } from "semantic-ui-react";
import { Input, Select, Button as AntButton, Tag as AntTag } from "antd";
import {
  LeftOutlined,
  CloseOutlined,
  PictureOutlined,
  UploadOutlined,
  CloseCircleFilled,
} from "@ant-design/icons";
import { useFormik } from "formik";
import { TiptapEditor } from "../../../TiptapEditor/TiptapEditor";
import {
  initialValuesOne,
  validationSchemaOne,
  categoryOptions,
  subcategoryOptions,
} from "./StepOne.form";
import { SceneFormFile } from "./SceneFormFile";
import { VirtualScenes } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { NEW_EDUCAMAKER } from "../../../../ui/featureFlags";
import { useTranslation } from "react-i18next";
import sx from "./SceneFormNew.module.scss";
import "./StepOne.scss";

const toAntOptions = (opts) =>
  (opts || []).map((o) => ({ label: o.text, value: o.value }));

const virtualSceneController = new VirtualScenes();

export function StepOne({
  formData,
  updateFormData,
  prevStep,
  isUpdateMode,
  scene,
  onSuccess, // <--- Nueva prop para éxito
  onClose, // <--- Esta ahora ejecuta handleSafeClose del padre
}) {
  const { accessToken } = useAuth();
  const { t } = useTranslation("scenes");
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [fileFieldName, setFileFieldName] = useState("");
  const [isPublic, setIsPublic] = useState(true);
  const [moduleData, setModuleData] = useState(null);
  const [uploadedFiles, setUploadedFiles] = useState({
    bannertop: formData.bannertop || null,
  });

  useEffect(() => {
    if (!isUpdateMode || !scene?.refId) return;
    (async () => {
      try {
        const data = await virtualSceneController.getVirtualScenesModeule(
          scene.refId,
          accessToken,
          "module_01",
        );
        setModuleData(data?.moduleData || null);
      } catch (error) {
        // Escena incompleta (sin module_01 todavia): degradamos a form vacio
        // en vez de romper la app.
        // eslint-disable-next-line no-console
        console.warn("No se pudo cargar module_01 de la escena:", error);
        setModuleData(null);
      }
    })();
  }, [isUpdateMode, accessToken, scene?.refId]);

  const formik = useFormik({
    initialValues: isUpdateMode
      ? initialValuesOne({ ...moduleData, refId: scene.refId })
      : initialValuesOne(formData),
    validationSchema: validationSchemaOne(),
    enableReinitialize: true,
    validateOnChange: false,
    onSubmit: async (values) => {
      const sceneId = isUpdateMode ? scene.refId : values.idScene;

      if (values.tagsInput) {
        values.ModOneTags = [...values.ModOneTags, values.tagsInput.trim()];
        values.tagsInput = "";
      }

      await virtualSceneController.createModuleAdd(
        "module_01",
        accessToken,
        sceneId,
        values,
      );

      updateFormData({ ...values, ...uploadedFiles });

      // <--- AQUÍ CAMBIAMOS LA LÓGICA DE CIERRE
      if (onSuccess) {
        onSuccess(); // Notificamos éxito al padre (no borra nada)
      } else {
        onClose(); // Fallback
      }
    },
  });

  // ... (El resto de funciones handleFileSubmit y handleRemoveFile siguen igual) ...
  const handleFileSubmit = (fileData) => {
    setUploadedFiles((prev) => ({ ...prev, [fileFieldName]: fileData }));
    formik.setFieldValue(fileFieldName, fileData.id);
    setIsFileModalOpen(false);
  };

  const handleRemoveFile = async () => {
    const fileId = uploadedFiles.bannertop?._id;
    if (!fileId) return;
    await virtualSceneController.deleteImageRepo(accessToken, fileId);
    setUploadedFiles({ bannertop: null });
    formik.setFieldValue("bannertop", null);
  };

  const addTag = () => {
    if (formik.values.tagsInput) {
      formik.setFieldValue("ModOneTags", [
        ...formik.values.ModOneTags,
        formik.values.tagsInput.trim(),
      ]);
      formik.setFieldValue("tagsInput", "");
    }
  };

  const removeTag = (idx) => {
    formik.setFieldValue(
      "ModOneTags",
      formik.values.ModOneTags.filter((_, i) => i !== idx),
    );
  };

  if (NEW_EDUCAMAKER) {
    return (
      <form className={sx.form} onSubmit={formik.handleSubmit}>
        <div className={sx.field}>
          <span className={sx.label}>{t("form.sceneTitleLabel")}</span>
          <Input
            name="ModOneTitle"
            size="large"
            placeholder={t("form.sceneTitlePlaceholder")}
            value={formik.values.ModOneTitle}
            onChange={formik.handleChange}
          />
          {formik.errors.ModOneTitle ? (
            <span className={sx.error}>{formik.errors.ModOneTitle}</span>
          ) : null}
        </div>

        <div className={sx.field}>
          <span className={sx.label}>{t("form.categoryLabel")}</span>
          <Select
            mode="multiple"
            size="large"
            allowClear
            options={toAntOptions(categoryOptions)}
            placeholder={t("form.categoryPlaceholder")}
            value={formik.values.ModOneCategory}
            onChange={(value) => formik.setFieldValue("ModOneCategory", value)}
          />
          {formik.errors.ModOneCategory ? (
            <span className={sx.error}>{formik.errors.ModOneCategory}</span>
          ) : null}
        </div>

        <div className={sx.field}>
          <span className={sx.label}>{t("form.subcategoryLabel")}</span>
          <Select
            mode="multiple"
            size="large"
            allowClear
            showSearch
            optionFilterProp="label"
            options={toAntOptions(subcategoryOptions)}
            placeholder={t("form.subcategoryPlaceholder")}
            value={formik.values.ModOneSubcategory}
            onChange={(value) =>
              formik.setFieldValue("ModOneSubcategory", value)
            }
          />
          {formik.errors.ModOneSubcategory ? (
            <span className={sx.error}>{formik.errors.ModOneSubcategory}</span>
          ) : null}
        </div>

        <div className={sx.field}>
          <span className={sx.label}>{t("form.tagsLabel")}</span>
          <div className={sx.tagsRow}>
            <Input
              name="tagsInput"
              size="large"
              placeholder={t("form.tagsPlaceholder")}
              value={formik.values.tagsInput}
              onChange={formik.handleChange}
              onPressEnter={(e) => {
                e.preventDefault();
                addTag();
              }}
            />
            <AntButton type="primary" size="large" onClick={addTag}>
              {t("form.addTag")}
            </AntButton>
          </div>
          {formik.values.ModOneTags.length ? (
            <div className={sx.tagsList}>
              {formik.values.ModOneTags.map((tag, i) => (
                <AntTag
                  key={`${tag}-${i}`}
                  closable
                  closeIcon={<CloseOutlined />}
                  onClose={(e) => {
                    e.preventDefault();
                    removeTag(i);
                  }}
                  color="green"
                >
                  {tag}
                </AntTag>
              ))}
            </div>
          ) : null}
          {formik.errors.ModOneTags ? (
            <span className={sx.error}>{formik.errors.ModOneTags}</span>
          ) : null}
        </div>

        <div className={sx.field}>
          <span className={sx.label}>{t("form.descriptionLabel")}</span>
          <div className={sx.editorBox}>
            <TiptapEditor
              content={formik.values.ModOneDescription}
              onChange={(newContent) =>
                formik.setFieldValue("ModOneDescription", newContent)
              }
            />
          </div>
          {formik.errors.ModOneDescription ? (
            <span className={sx.error}>{formik.errors.ModOneDescription}</span>
          ) : null}
        </div>

        <div className={sx.field}>
          <span className={sx.label}>{t("form.urlSalaLabel")}</span>
          <Input
            name="ModOneUrlsala"
            size="large"
            placeholder="https://"
            value={formik.values.ModOneUrlsala}
            onChange={formik.handleChange}
          />
          {formik.errors.ModOneUrlsala ? (
            <span className={sx.error}>{formik.errors.ModOneUrlsala}</span>
          ) : null}
        </div>

        <div className={sx.field}>
          <span className={sx.label}>{t("form.coverImageLabel")}</span>
          <div className={sx.coverRow}>
            <AntButton
              icon={<UploadOutlined />}
              onClick={() => {
                setFileFieldName("bannertop");
                setIsPublic(true);
                setIsFileModalOpen(true);
              }}
            >
              {t("form.selectImage")}
            </AntButton>

            {uploadedFiles.bannertop ? (
              <div className={sx.preview}>
                <img src={uploadedFiles.bannertop.url} alt="" />
                <span
                  className={sx.removeImg}
                  role="button"
                  tabIndex={0}
                  onClick={handleRemoveFile}
                >
                  <CloseCircleFilled />
                </span>
              </div>
            ) : (
              <PictureOutlined style={{ fontSize: 22, color: "#9aa5b1" }} />
            )}
          </div>
          {formik.errors.bannertop ? (
            <span className={sx.error}>{formik.errors.bannertop}</span>
          ) : null}
        </div>

        <SceneFormFile
          isOpen={isFileModalOpen}
          onClose={() => setIsFileModalOpen(false)}
          onSubmit={handleFileSubmit}
          isPublic={isPublic}
          idScene={formik.values.idScene}
          accept="image/*"
          buttonText={t("form.selectImage")}
        />

        <div className={sx.footer}>
          <AntButton icon={<LeftOutlined />} onClick={prevStep}>
            {t("form.prevButton")}
          </AntButton>
          <AntButton danger onClick={onClose}>
            {t("form.cancelExitButton")}
          </AntButton>
          <AntButton
            type="primary"
            htmlType="submit"
            loading={formik.isSubmitting}
          >
            {t("form.submitButton")}
          </AntButton>
        </div>
      </form>
    );
  }

  return (
    <Form className="step-one-form" onSubmit={formik.handleSubmit}>
      {/* ... (Todo el JSX de inputs se mantiene IDÉNTICO hasta los botones) ... */}

      <Form.Input
        label={t("form.sceneTitleLabel")}
        name="ModOneTitle"
        placeholder={t("form.sceneTitlePlaceholder")}
        onChange={formik.handleChange}
        value={formik.values.ModOneTitle}
        error={formik.errors.ModOneTitle}
      />

      <Form.Select
        label={t("form.categoryLabel")}
        name="ModOneCategory"
        options={categoryOptions}
        placeholder={t("form.categoryPlaceholder")}
        multiple
        value={formik.values.ModOneCategory}
        onChange={(_, { name, value }) => formik.setFieldValue(name, value)}
        error={formik.errors.ModOneCategory}
      />

      <Form.Select
        label={t("form.subcategoryLabel")}
        name="ModOneSubcategory"
        options={subcategoryOptions}
        placeholder={t("form.subcategoryPlaceholder")}
        multiple
        value={formik.values.ModOneSubcategory}
        onChange={(_, { name, value }) => formik.setFieldValue(name, value)}
        error={formik.errors.ModOneSubcategory}
      />

      <div className="tags-container">
        {formik.values.ModOneTags.map((tag, i) => (
          <Label key={i}>{tag}</Label>
        ))}
      </div>

      <Form.Input
        label={t("form.tagsLabel")}
        name="tagsInput"
        placeholder={t("form.tagsPlaceholder")}
        value={formik.values.tagsInput}
        onChange={formik.handleChange}
        error={formik.errors.ModOneTags}
      />
      <Button
        type="button"
        secondary
        className="add-tag-btn"
        onClick={() => {
          if (formik.values.tagsInput) {
            formik.setFieldValue("ModOneTags", [
              ...formik.values.ModOneTags,
              formik.values.tagsInput.trim(),
            ]);
            formik.setFieldValue("tagsInput", "");
          }
        }}
      >
        {t("form.addTag")}
      </Button>

      <label className="field-label">{t("form.descriptionLabel")}</label>
      <TiptapEditor
        content={formik.values.ModOneDescription}
        onChange={(newContent) =>
          formik.setFieldValue("ModOneDescription", newContent)
        }
      />

      <Form.Input
        label={t("form.urlSalaLabel")}
        name="ModOneUrlsala"
        placeholder="https://"
        onChange={formik.handleChange}
        value={formik.values.ModOneUrlsala}
        error={formik.errors.ModOneUrlsala}
      />

      <div className="upload-field">
        <label>{t("form.coverImageLabel")}</label>
        <Button
          type="button"
          onClick={() => {
            setFileFieldName("bannertop");
            setIsPublic(true);
            setIsFileModalOpen(true);
          }}
        >
          {t("form.selectImage")}
        </Button>

        {uploadedFiles.bannertop && (
          <div className="image-preview">
            <Image src={uploadedFiles.bannertop.url} size="small" centered />
            <Icon
              name="close"
              onClick={handleRemoveFile}
              className="close-icon"
            />
          </div>
        )}

        {formik.errors.bannertop && (
          <div className="error-message">{formik.errors.bannertop}</div>
        )}
      </div>

      <SceneFormFile
        isOpen={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        onSubmit={handleFileSubmit}
        isPublic={isPublic}
        idScene={formik.values.idScene}
        accept="image/*"
        buttonText={t("form.selectImage")}
      />

      <Form.Group className="form-buttons">
        <Button type="button" color="orange" onClick={prevStep}>
          {t("form.prevButton")}
        </Button>
        {/* Este botón ahora llama a onClose, que es el handleSafeClose del padre */}
        <Button type="button" color="red" onClick={onClose}>
          {t("form.cancelExitButton")}
        </Button>
        <Button type="submit" primary loading={formik.isSubmitting}>
          {t("form.submitButton")}
        </Button>
      </Form.Group>
    </Form>
  );
}
